/**
 * VersionListItem is a component that displays a revision in version history lists.
 * It is used in the VersionHistory and PageEditorRevisions components.
 */

import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client/react'

import { FileIcon } from '@pleio/icons/file'
import { FileArchiveIcon } from '@pleio/icons/file-archive'
import { FilePublishIcon } from '@pleio/icons/file-publish'

import { useToast } from 'js/components/Toast'
import Version from 'js/components/Version'

import useVersionListItemAction from '../components/useVersionListItemAction'

import { updateRevisionDescriptionCache } from './updateRevisionDescriptionCache'
import { UPDATE_REVISION } from './VersionListItem.queries'
import type { VersionListItemProps } from './VersionListItem.types'

const VersionListItem = ({
    revision,
    url,
    isActive,
    isLatest,
    onRestore,
}: VersionListItemProps) => {
    const { t } = useTranslation()
    const { showToast } = useToast()

    const { guid, type, description, timeCreated, statusPublishedChanged } =
        revision

    const [updateRevision, { loading }] = useMutation(UPDATE_REVISION, {
        update(cache, { data }) {
            updateRevisionDescriptionCache(cache, data?.updateRevision)
        },
    })

    const handleSubmitDescription = (description: string) => {
        return updateRevision({
            variables: {
                input: {
                    guid,
                    description,
                },
            },
        }).catch((error) => {
            console.error(error)
            showToast({
                title: t('error.title'),
                message: t('version.save-description-error'),
                color: 'warn',
            })
            throw error
        })
    }

    const isPublished = statusPublishedChanged === 'published'
    const isArchived = statusPublishedChanged === 'archived'
    const actionLabel = useVersionListItemAction(revision)

    return (
        <Version
            url={url}
            description={description}
            timeCreated={timeCreated}
            isActive={isActive}
            isLatest={isLatest}
            icon={
                isPublished ? (
                    <FilePublishIcon />
                ) : isArchived ? (
                    <FileArchiveIcon />
                ) : type === 'create' ? (
                    <FileIcon />
                ) : null
            }
            actionLabel={actionLabel}
            onSubmitDescription={handleSubmitDescription}
            loading={loading}
            onRestore={isActive && !isLatest ? onRestore : undefined}
        />
    )
}

export default VersionListItem
